/**
 * Centralized API error handling utilities.
 * Use across all API modules for consistent error extraction and user-facing messages.
 */

const HTTP_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE: 422,
  SERVER_ERROR: 500,
};

const USER_MESSAGES = {
  [HTTP_STATUS.UNAUTHORIZED]: "Session expired or invalid. Please log in again.",
  [HTTP_STATUS.FORBIDDEN]: "You don't have permission to perform this action.",
  [HTTP_STATUS.NOT_FOUND]: "The requested resource was not found.",
  [HTTP_STATUS.CONFLICT]: "This action conflicts with existing data.",
  [HTTP_STATUS.SERVER_ERROR]: "Server is temporarily unavailable. Please try again later.",
  network: "Network error. Please check your connection and try again.",
  timeout: "Request timed out. Please try again.",
};

/**
 * Server codes meaning "this session is over because of who you are now" —
 * the account was suspended or no longer exists. Distinct from an ordinary 401
 * (expired token) or 403 (you may not do this particular thing), both of which
 * the user can recover from without being signed out.
 *
 * Kept in step with ACCOUNT_SUSPENDED_CODE / ACCOUNT_NOT_FOUND_CODE in
 * server/auth/authService.js.
 */
export const REVOKED_SESSION_CODES = ["ACCOUNT_SUSPENDED", "ACCOUNT_NOT_FOUND"];

/**
 * Extract HTTP status code from axios/API error.
 */
export const getHttpStatus = (error) => {
  if (!error) return null;
  return error?.response?.status ?? error?.status ?? null;
};

/**
 * A load's rejection that keeps the server's status beside its message
 * (E2E audit 5·9, 5·19). A page has to tell "this does not exist" from
 * "this could not be loaded", and a bare message string cannot carry that.
 * `message` is the same text the string used to be.
 */
export const loadRejection = (error, fallback) => {
  const rejection = new Error(extractError(error, fallback));
  rejection.httpStatus = getHttpStatus(error) ?? error?.httpStatus ?? null;
  return rejection;
};

/**
 * The server answered that the thing asked for is not there: 404, or 400 for
 * an id it could not read. Offline, a timeout or a server error is not this,
 * and a page should offer to try again instead.
 */
export const isGoneRejection = (err) => err?.httpStatus === 404 || err?.httpStatus === 400;

/**
 * True when the server has told us this session can never succeed again.
 *
 * Without this the app keeps polling notifications every 30 seconds and shows
 * the user an error snackbar each time, forever — a suspended account looks
 * broken rather than suspended. Matched on the explicit code, never on the
 * status alone, so a routine permission failure does not sign anyone out.
 */
export const isRevokedSession = (error) => {
  const status = getHttpStatus(error);
  if (status !== 401 && status !== 403) return false;
  const code = error?.response?.data?.code;
  return REVOKED_SESSION_CODES.includes(code);
};

/**
 * Q-31: the server's answer to a build older than MIN_APP_VERSION_CODE. Kept in
 * step with APP_UPDATE_REQUIRED_CODE in server/middleware/appVersionGate.js.
 */
export const APP_UPDATE_REQUIRED_CODE = "APP_UPDATE_REQUIRED";

/**
 * True when the server has said this build is too old to serve. Matched on the
 * status AND the explicit code, like isRevokedSession: a 426 from anything
 * else (a proxy's) must not put up a screen nobody can get past.
 */
export const isAppUpdateRequired = (error) =>
  getHttpStatus(error) === 426 && error?.response?.data?.code === APP_UPDATE_REQUIRED_CODE;

/**
 * Check if error is retryable (network/timeout/5xx).
 */
export const isRetryable = (error) => {
  const status = getHttpStatus(error);
  if (status && status >= 500) return true;
  const msg = String(error?.message || "").toLowerCase();
  return /network|timeout|econnreset|enotfound|failed to fetch|load failed/i.test(msg);
};

/**
 * Extract user-facing error message from various error shapes.
 * Handles: axios errors, string errors, server { error, message } payloads.
 */
export const extractError = (error, fallback = "An unexpected error occurred") => {
  if (error == null) return fallback;
  if (typeof error === "string") return error || fallback;

  const status = getHttpStatus(error);
  const data = error?.response?.data;

  let message = null;

  if (data) {
    if (typeof data === "string") message = data;
    else message = data?.error ?? data?.message ?? data?.msg;
  }

  if (message && typeof message === "string") return message;
  if (error?.userMessage) return error.userMessage;

  // The curated table has to be consulted BEFORE `error.message`, not after.
  //
  // Every axios failure carries a message, so an `error.message` fallback ahead
  // of these branches returns for all of them and nothing below it can ever run.
  // That made USER_MESSAGES.network and USER_MESSAGES.timeout dead code, and
  // what reached the user instead was the library's own diagnostic: a top-right
  // alert reading exactly "Network Error", or "timeout of 5000ms exceeded".
  // Measured on the running app, on /map, with discovery failing.
  //
  // The server's own message still wins above — `handleError` sanitises it
  // through normalizeErrorMessage, so it is written for a user. These branches
  // only catch failures that never reached the server, where the only string on
  // offer was written for a developer.
  if (
    error?.code === "ECONNABORTED" ||
    error?.message?.toLowerCase?.().includes("timeout")
  ) {
    return USER_MESSAGES.timeout;
  }

  if (error?.code === "ERR_NETWORK" || !error?.response) {
    return USER_MESSAGES.network;
  }

  if (status && USER_MESSAGES[status]) return USER_MESSAGES[status];

  if (error?.message) return error.message;

  return fallback;
};

/**
 * Create a normalized error object for consistent handling.
 *
 * `code` is the server's own machine-readable reason, and it is here because a
 * status alone is ambiguous (#102): the auth middleware answers a suspended
 * account with 403, and the discovery policy answers a request from outside the
 * beta launch area with 403 too. A caller deciding what to SAY has to tell them
 * apart, and reaching through `original.response.data.code` to do it makes the
 * axios shape part of every caller's contract. Deliberately only the server's
 * `code` — an axios transport code like ERR_NETWORK is a different vocabulary
 * and merging the two here would make neither trustworthy.
 */
export const normalizeError = (error, fallback = "An unexpected error occurred") => ({
  message: extractError(error, fallback),
  status: getHttpStatus(error),
  code: error?.response?.data?.code ?? null,
  retryable: isRetryable(error),
  original: error,
});
